import { useState, useRef } from "react";
import { Printer, Search, BookText, Lightbulb, ListChecks, Download, Layers, List, Columns, HelpCircle, Eye, EyeOff, Palette, GraduationCap, UserCircle2 } from "lucide-react";
import html2pdf from "html2pdf.js";
import { Flashcards } from "@/components/Flashcards";
import { CornellNotes } from "@/components/CornellNotes";
import { AssessmentExtras } from "@/components/AssessmentExtras";
import { useAuth } from "@/context/AuthContext";

// Three print themes. Each pairs a cover-page treatment with a body class so
// the printed PDF actually feels intentional — not "web page dumped to PDF".
const THEMES = {
  classic: {
    id: "classic",
    label: "Classic",
    hint: "Serif · warm cream cover",
    coverBg: "bg-gradient-to-br from-primary via-primary/90 to-primary/70 text-primary-foreground",
    coverFont: "font-serif",
    bodyFont: "font-serif",
    accent: "text-accent",
  },
  elegant: {
    id: "elegant",
    label: "Elegant",
    hint: "Editorial · deep sage & terracotta",
    coverBg: "bg-gradient-to-br from-emerald-900 via-emerald-800 to-orange-700 text-white",
    coverFont: "font-serif italic",
    bodyFont: "font-serif",
    accent: "text-orange-300",
  },
  minimal: {
    id: "minimal",
    label: "Minimal",
    hint: "Modern · black on white",
    coverBg: "bg-white text-black border-b-4 border-black",
    coverFont: "font-sans",
    bodyFont: "font-sans",
    accent: "text-black",
  },
};

const PrintCover = ({ theme, lecture }) => {
  const t = THEMES[theme];
  return (
    <div
      data-testid={`print-cover-${theme}`}
      className={`hidden print:flex ${t.coverBg} min-h-[95vh] flex-col justify-between p-14 print-break-after`}
      style={{ pageBreakAfter: "always" }}
    >
      <div>
        <p className={`text-[11px] font-bold uppercase tracking-[0.4em] opacity-80 ${t.coverFont}`}>
          Study Guide
        </p>
        <p className={`mt-2 text-[13px] uppercase tracking-[0.25em] ${t.accent}`}>
          {lecture.book_title}
        </p>
      </div>
      <div>
        <h1 className={`${t.coverFont} text-5xl sm:text-6xl font-bold leading-[1.05] tracking-tight`}>
          {lecture.title}
        </h1>
        <div className="mt-6 flex flex-wrap gap-3 text-[13px] opacity-90">
          {lecture.chapters && <span>· {lecture.chapters}</span>}
          {lecture.edition && <span>· {lecture.edition}</span>}
          {lecture.depth && <span>· {String(lecture.depth).replace(/^./, (c) => c.toUpperCase())} depth</span>}
        </div>
      </div>
      <div className="flex items-end justify-between gap-6">
        <div>
          <p className={`text-[10px] uppercase tracking-[0.3em] opacity-70 ${t.coverFont}`}>
            Prepared with
          </p>
          <p className={`mt-1 text-2xl font-bold ${t.coverFont}`}>
            Lectura<span className={t.accent}>.</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">
            Standards-aligned
          </p>
          <p className="mt-1 text-[11px] font-semibold">Common Core · NGSS · TEKS</p>
        </div>
      </div>
    </div>
  );
};

export const StudyGuide = ({ lecture }) => {
  const { user } = useAuth();
  const isInstructor = user && (user.role === "instructor" || user.role === "admin");
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("list");
  const [layout, setLayout] = useState("standard");
  const [showAllAnswers, setShowAllAnswers] = useState(false);
  const [openQA, setOpenQA] = useState({});
  const [theme, setTheme] = useState(() => localStorage.getItem("lectura_guide_theme") || "classic");
  // Teacher Key mode — instructors can toggle to reveal model answers / rubrics for the extras
  // and get a "Teacher Key" PDF with watermarked cover; students never see this toggle.
  const [teacherMode, setTeacherMode] = useState(false);
  const pdfRef = useRef(null);

  const changeTheme = (t) => {
    setTheme(t);
    localStorage.setItem("lectura_guide_theme", t);
  };

  const downloadPdf = (asTeacher = false) => {
    if (!pdfRef.current) return;
    // Temporarily force teacherMode so the printed DOM contains model answers.
    const prev = teacherMode;
    setTeacherMode(asTeacher);
    const suffix = asTeacher ? "TEACHER_KEY" : "study_guide";
    // defer one tick so the DOM updates with the new state before html2pdf snapshots it
    setTimeout(() => {
      html2pdf()
        .set({
          margin: 12,
          filename: `${(lecture.title || "study-guide").replace(/[^a-z0-9]+/gi, "_")}__${suffix}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "avoid-all"] },
        })
        .from(pdfRef.current)
        .save()
        .then(() => setTeacherMode(prev));
    }, 150);
  };

  const terms = (lecture.key_terms || []).filter(
    (t) =>
      t.term.toLowerCase().includes(q.toLowerCase()) ||
      t.definition.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="no-print flex items-center justify-between mb-8">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
            Printable Study Guide
          </span>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-bold tracking-tight">
            {lecture.title}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-border p-0.5" data-testid="guide-theme-picker">
            {Object.values(THEMES).map((t) => (
              <button
                key={t.id}
                data-testid={`theme-${t.id}`}
                onClick={() => changeTheme(t.id)}
                title={t.hint}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  theme === t.id ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                }`}
              >
                <Palette className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-full border border-border p-0.5">
            <button
              data-testid="layout-standard"
              onClick={() => setLayout("standard")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                layout === "standard" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              <List className="h-3.5 w-3.5" /> Standard
            </button>
            <button
              data-testid="layout-cornell"
              onClick={() => setLayout("cornell")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                layout === "cornell" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              <Columns className="h-3.5 w-3.5" /> Cornell
            </button>
          </div>
          <button
            data-testid="btn-download-pdf"
            onClick={() => downloadPdf(false)}
            className="flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            <Download className="h-4 w-4" /> Student PDF
          </button>
          {isInstructor && (
            <button
              data-testid="btn-download-teacher-pdf"
              onClick={() => downloadPdf(true)}
              className="flex items-center gap-2 rounded-full border-2 border-accent text-accent px-5 py-2.5 text-sm font-semibold hover:bg-accent/5 transition-colors"
              title="PDF with model answers, rubrics, and the full teacher key"
            >
              <GraduationCap className="h-4 w-4" /> Teacher Key PDF
            </button>
          )}
          <button
            data-testid="btn-print-study-guide"
            onClick={() => window.print()}
            className="flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/50 transition-colors"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>
      </div>

      {/* Teacher-mode toggle — only visible to instructors; flips model answers on/off for the on-screen view */}
      {isInstructor && (
        <div data-testid="teacher-mode-row" className="no-print -mt-4 mb-6 flex items-center justify-end">
          <button
            onClick={() => setTeacherMode((v) => !v)}
            data-testid="toggle-teacher-mode"
            className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-xs font-semibold transition ${teacherMode ? "border-accent bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:border-accent/40"}`}
          >
            {teacherMode ? <GraduationCap className="h-3.5 w-3.5" /> : <UserCircle2 className="h-3.5 w-3.5" />}
            {teacherMode ? "Teacher view · answers visible" : "Student view · answers hidden"}
          </button>
        </div>
      )}

      <div ref={pdfRef} className={`print-area space-y-10 ${THEMES[theme].bodyFont}`}>
        <PrintCover theme={theme} lecture={lecture} />
        <div className="hidden print:block mb-6">
          <h1 className="font-serif text-3xl font-bold">{lecture.title}</h1>
          <p className="text-sm text-muted-foreground">
            {lecture.book_title} · {lecture.chapters || "Whole book"}
          </p>
        </div>

        {layout === "cornell" ? (
          <CornellNotes lecture={lecture} />
        ) : (
        <>
        {/* Summary */}
        <section className="print-break rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-3 text-accent">
            <BookText className="h-4 w-4" />
            <h2 className="font-serif text-2xl font-semibold text-foreground">Executive Summary</h2>
          </div>
          <p className="text-base leading-relaxed text-foreground/85">{lecture.summary}</p>
          {lecture.edition && (
            <p className="mt-3 text-xs font-semibold text-muted-foreground">Based on: {lecture.edition}</p>
          )}
        </section>

        {/* Key Concepts */}
        <section className="print-break">
          <div className="flex items-center gap-2 mb-4 text-accent">
            <Lightbulb className="h-4 w-4" />
            <h2 className="font-serif text-2xl font-semibold text-foreground">Key Concepts</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {(lecture.key_concepts || []).map((c, i) => (
              <div
                key={i}
                data-testid={`concept-${i}`}
                className="rounded-xl border border-border bg-card p-5 shadow-sm hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center h-7 w-7 rounded-lg bg-highlight text-primary font-serif font-bold text-sm shrink-0" style={{ background: "hsl(var(--secondary))" }}>
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-base text-foreground">{c.concept}</h3>
                    <p className="mt-1 text-sm text-foreground/75 leading-relaxed">{c.explanation}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Source Map */}
        {lecture.citations && lecture.citations.length > 0 && (
          <section className="print-break" data-testid="source-map">
            <div className="flex items-center gap-2 mb-4 text-accent">
              <BookText className="h-4 w-4" />
              <h2 className="font-serif text-2xl font-semibold text-foreground">
                Source Map{lecture.edition ? ` · ${lecture.edition}` : ""}
              </h2>
            </div>
            <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
              {lecture.citations.map((c, i) => (
                <div key={i} className="px-5 py-3 border-b border-border last:border-0">
                  <p className="text-sm font-medium text-foreground">{c.point}</p>
                  {(c.section || c.pages) && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {c.section}{c.section && c.pages ? " · " : ""}{c.pages}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground italic">
              Section references follow the {lecture.edition || "latest"} edition; page numbers are approximate and may vary by printing.
            </p>
          </section>
        )}

        {/* Key Terms */}
        <section className="print-break">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-accent">
              <ListChecks className="h-4 w-4" />
              <h2 className="font-serif text-2xl font-semibold text-foreground">Key Terms</h2>
            </div>
            <div className="no-print flex items-center gap-2">
              <div className="flex rounded-full border border-border p-0.5">
                <button
                  data-testid="terms-mode-list"
                  onClick={() => setMode("list")}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    mode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  <List className="h-3.5 w-3.5" /> Glossary
                </button>
                <button
                  data-testid="terms-mode-flashcards"
                  onClick={() => setMode("flashcards")}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    mode === "flashcards" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  <Layers className="h-3.5 w-3.5" /> Flashcards
                </button>
              </div>
              {mode === "list" && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    data-testid="glossary-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter terms…"
                    className="rounded-full border border-input bg-background pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/60"
                  />
                </div>
              )}
            </div>
          </div>

          {mode === "flashcards" ? (
            <Flashcards terms={lecture.key_terms || []} />
          ) : (
            <div className="rounded-2xl border border-border overflow-hidden bg-card shadow-sm">
            {terms.map((t, i) => (
              <div
                key={i}
                className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors"
              >
                <dt className="font-semibold text-foreground font-mono text-sm">{t.term}</dt>
                <dd className="sm:col-span-2 text-sm text-foreground/80 leading-relaxed">{t.definition}</dd>
              </div>
            ))}
            {terms.length === 0 && (
              <p className="px-5 py-6 text-sm text-muted-foreground text-center">No terms match "{q}".</p>
            )}
          </div>
          )}
        </section>

        {/* NEW — 7 Content Volume Picker extras: short-answer, fill-blanks, recall, discussion,
            critical thinking, legal/ethical, eye-to-future. All honor teacherMode. */}
        <AssessmentExtras lecture={lecture} teacherMode={teacherMode} />

        {/* End-of-Chapter Questions & Model Answers */}
        {(lecture.end_of_chapter_qas || []).length > 0 && (
          <section className="print-break" data-testid="eoc-qa-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 text-accent">
                <HelpCircle className="h-4 w-4" />
                <h2 className="font-serif text-2xl font-semibold text-foreground">End-of-Chapter Questions</h2>
                <span className="rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Recall · Critical Thinking</span>
              </div>
              <button
                data-testid="toggle-all-answers"
                onClick={() => {
                  const next = !showAllAnswers;
                  setShowAllAnswers(next);
                  const map = {};
                  (lecture.end_of_chapter_qas || []).forEach((_, i) => { map[i] = next; });
                  setOpenQA(map);
                }}
                className="no-print inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-1.5 text-xs font-semibold hover:bg-secondary/60 transition"
              >
                {showAllAnswers ? <><EyeOff className="h-3.5 w-3.5" /> Hide all answers</> : <><Eye className="h-3.5 w-3.5" /> Reveal all answers</>}
              </button>
            </div>
            <ul className="space-y-3">
              {(lecture.end_of_chapter_qas || []).map((qa, i) => (
                <li key={i} data-testid={`eoc-qa-${i}`} className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                  <div className="px-5 py-4">
                    <div className="flex items-start gap-3">
                      <span className="grid place-items-center h-7 w-7 rounded-lg font-serif font-bold text-sm shrink-0 text-primary" style={{ background: "hsl(var(--secondary))" }}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {qa.kind && (
                          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1">{qa.kind}</p>
                        )}
                        <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">{qa.question}</p>
                      </div>
                    </div>
                    <button
                      data-testid={`eoc-toggle-${i}`}
                      onClick={() => setOpenQA((s) => ({ ...s, [i]: !s[i] }))}
                      className="no-print mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:opacity-80 transition"
                    >
                      {openQA[i] ? <><EyeOff className="h-3.5 w-3.5" /> Hide answer</> : <><Eye className="h-3.5 w-3.5" /> Show model answer</>}
                    </button>
                  </div>
                  {(openQA[i] || showAllAnswers) && (
                    <div data-testid={`eoc-answer-${i}`} className="border-t border-accent/30 bg-accent/5 px-5 py-4">
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1.5">Model answer</p>
                      <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{qa.answer}</p>
                    </div>
                  )}
                  {/* Print-view: always show answer when printing */}
                  <div className="hidden print:block border-t border-accent/30 bg-accent/5 px-5 py-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent mb-1.5">Answer</p>
                    <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{qa.answer}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        </>
        )}
      </div>
    </div>
  );
};
